import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import type { IAsset } from "@api/IWalletController";
import { initialAssets, useAssets } from "./assets.store";
import { WalletController } from "@api/WalletController";
import { useWallet } from "./wallet.store";


export interface IAssetInfo {
    asset: IAsset | null;
    amount: string;
    balance: number;
    price: number | null;
}

interface MemeSwapStateData {
    assetInRateLimit?: boolean; // UI flag for TON API rate limit
    assetInInfo: {
        asset: IAsset | null;
        amount: string;
        balance: number;
        price: number | null;
    };
    assetOutInfo: {
        asset: IAsset | null;
        amount: string;
        balance: number;
        price: number | null;
    };

    memeSwapTargetAddress: string;
    pad: string;
    slippagePercentage: number;
    isMeme: boolean;
}

interface MemeSwapStateActions {
    setMemeSwapAsset: (asset: IAsset | null) => void;
    setAssetIn: (asset: IAsset | null) => void;

    setAssetOut: (asset: IAsset | null) => void;

    setAssetInAmount: (amount: string) => void;
    setMemeSwapTargetAddress: (address: string) => Promise<void>; // Now async
    setPad: (pad: string) => void;
    updateMemeSwapAssetBalance: () => Promise<void>;
    updateMemeSwapInputAssetBalance: () => Promise<void>;
    updateAllMemeSwapBalances: () => Promise<void>;
    setSlippagePercentage: (slippage: number) => void;
    executeSwap: (type?: "internal" | "external") => Promise<any>;
    checkIsMemeWithPools: () => void;
    setIsMeme: (isMeme: boolean) => void;   
}

type MemeSwapState = MemeSwapStateData & MemeSwapStateActions;



const initialMemeSwapStateData: MemeSwapStateData = {
    assetInRateLimit: false,
    assetInInfo: {
        asset: initialAssets[0],
        balance: 0,
        amount: "1", // Set initial sell amount to 1
        price: null,
    },
    assetOutInfo: {
        asset: initialAssets?.length > 1 ? initialAssets[0] : (initialAssets?.[0] ?? null),
        amount: "",
        balance: 0,
        price: null,
    },

    memeSwapTargetAddress: "",
    pad: "pumpfun",
    slippagePercentage: 0.5, // Default slippage
    isMeme: false,
};

// ---- Efficient balance cache and helpers (outside zustand store) ----
const _balanceCache: Record<string, { balance: number; timestamp: number }> = {};
const _BALANCE_CACHE_TTL = 15000; // 15 seconds
function _getBalanceCacheKey(walletGuid: string, assetGuid: string | undefined) {
    return walletGuid + "_" + (assetGuid || "TON");
}
async function _fetchAssetBalance(walletGuid: string, asset: IAsset, friendlyAddress: string | undefined): Promise<number> {
    const isTon = asset.symbol === "TON" || asset.name === "Toncoin";
    const cacheKey = _getBalanceCacheKey(walletGuid, asset.guid);
    const now = Date.now();
    // Use cache if valid
    if (_balanceCache[cacheKey] && now - _balanceCache[cacheKey].timestamp < _BALANCE_CACHE_TTL) {
        return _balanceCache[cacheKey].balance;
    }
    let balance = 0;
    try {
        if (isTon && friendlyAddress) {
            const TonApi = (await import("@api/TonApi")).TonApi;
            const tonApi = new TonApi();
            const tonAccount = await tonApi.getTonBalance(friendlyAddress);
            balance = tonAccount.balance ? Number(tonAccount.balance) / 1e9 : 0;
        } else {
            const response = await WalletController.getBalances(walletGuid, [asset]);
            if (response.status === "success" && response.data.length > 0) {
                const assetBalanceData = response.data[0];
                if (assetBalanceData.balances && Array.isArray(assetBalanceData.balances)) {
                    const jetton = assetBalanceData.balances.find(
                        (b: any) => b.jettonMasterAddress === asset.jettonMasterAddress,
                    );
                    balance = jetton?.amount ? Number(jetton.amount) : 0;
                }
            }
        }
    } catch (e) {
        balance = 0;
    }
    // Update cache
    _balanceCache[cacheKey] = { balance, timestamp: now };
    return balance;
}
// ---- End helpers ----

export const useMemeSwap = create<MemeSwapState>()(
    subscribeWithSelector((set, get) => ({

        checkIsMemeWithPools: () => {
            const pools = require("@/stores/pools.store").usePools.getState().pools;
            // New logic: if there are no pools, it's a meme
            set({ isMeme: pools && pools.length === 0 });
        },

        setIsMeme: (isMeme: boolean) => {
            set({ isMeme });
        },
        ...initialMemeSwapStateData,

        setMemeSwapAsset: (asset: IAsset | null) => {
    set((state) => ({
        assetOutInfo: {
            ...state.assetOutInfo,
            asset,
            amount: "",
            price: null,
            balance: 0,
        },
    }));
    // Trigger all balance updates for consistency
    get().updateAllMemeSwapBalances();
},

setAssetIn: (asset: IAsset | null) => {
    // If asset is null, fallback to TON
    const fallbackAsset = asset || initialAssets[0];
    set((state) => ({
        assetInInfo: {
            ...state.assetInInfo,
            asset: fallbackAsset,
            amount: "",
            balance: 0,
            price: null,
        },
    }));
    // Trigger all balance updates for consistency
    get().updateAllMemeSwapBalances();
},

        setAssetInAmount: (amount: string) => {
    set((state) => ({
        assetInInfo: { ...state.assetInInfo, amount },
    }));
},

        setAssetOut: (asset: IAsset | null) => {
    set((state) => ({
        assetOutInfo: {
            ...state.assetOutInfo,
            asset: asset || null,
            amount: "",
            balance: 0,
            price: null,
        },
    }));
    get().updateAllMemeSwapBalances();
},

        setMemeSwapTargetAddress: async (address: string) => {
            // Always set the address immediately
            set(() => ({
                memeSwapTargetAddress: address,
            }));

            // Try to find the asset, but do not block the address update
            try {
                const foundAssets = await useAssets.getState().findAssets([{ jettonMasterAddress: address }]);
                const foundAsset = foundAssets && foundAssets.length > 0 ? foundAssets[0] : null;
                console.log("[MemeSwap] Asset found for address", address, ":", foundAsset);
                set((state) => ({
                    assetOutInfo: {
                        ...state.assetOutInfo,
                        asset: foundAsset,
                    },
                }));
            } catch (err) {
                // Optionally log the error, but do not revert the address
                console.error("Error looking up asset:", err);
            }
        },

        setPad: (pad: string) => {
            set(() => ({ pad }));
        },

        setSlippagePercentage: (slippage: number) => {
            // Clamp slippage between 0 and 50
            const value = Math.max(0, Math.min(50, slippage));
            set(() => ({ slippagePercentage: value }));
        },

        updateMemeSwapAssetBalance: async () => {
            const { assetOutInfo } = get();
            const { currentWallet } = useWallet.getState();
            if (!assetOutInfo.asset || !currentWallet?.guid) {
                set((state: any) => ({
                    assetOutInfo: { ...state.assetOutInfo, balance: 0 },
                }));
                return;
            }
            // Always bypass cache after wallet or asset change for fresh fetch
            const balance = await _fetchAssetBalance(
                currentWallet.guid,
                assetOutInfo.asset,
                currentWallet.friendlyAddress
            );
            set((state: any) => ({
                assetOutInfo: { ...state.assetOutInfo, balance },
            }));
        },

        // Efficient input asset balance update
        updateMemeSwapInputAssetBalance: async () => {
            const { assetInInfo } = get();
            const { currentWallet } = useWallet.getState();
            if (!assetInInfo.asset || !currentWallet?.guid) {
                set((state: any) => ({
                    assetInInfo: { ...state.assetInInfo, balance: 0 },
                }));
                return;
            }
            const balance = await _fetchAssetBalance(
                currentWallet.guid,
                assetInInfo.asset,
                currentWallet.friendlyAddress
            );
            set((state: any) => ({
                assetInInfo: { ...state.assetInInfo, balance },
            }));
        },

        updateAllMemeSwapBalances: async () => {
            await get().updateMemeSwapAssetBalance();
            await get().updateMemeSwapInputAssetBalance();
        },

        executeSwap: async (type?: "internal" | "external") => {
            const { assetInInfo, assetOutInfo, memeSwapTargetAddress, slippagePercentage, pad } = get();
            const { currentWallet, externalWallet } = useWallet.getState();

            // Always use the currentWallet for swap execution
            const walletGuid = currentWallet?.guid;
            if (!walletGuid) {
                console.error("No wallet connected. Cannot execute swap.");
                return { status: "error", message: "No wallet connected." };
            }

            const asset = assetOutInfo.asset;
            if (!asset || !asset.guid) {
                return { status: "error", message: "No asset selected." };
            }
            if (!memeSwapTargetAddress) {
                return { status: "error", message: "No target address specified." };
            }

            // Ensure we have valid guids for both assets
            if (!assetInInfo.asset?.guid) {
                console.error("Input asset has no guid");
                return { status: "error", message: "Input asset has no guid" };
            }
            
            // Check if we're trying to swap TON for TON
            if (assetInInfo.asset.symbol === "TON" && assetOutInfo.asset?.symbol === "TON") {
                console.error("Cannot swap TON for TON");
                return { status: "error", message: "Cannot swap TON for TON" };
            }
            
            // Log the assets being swapped for debugging
            console.log("Swapping assets:", {
                input: assetInInfo.asset.symbol,
                output: assetOutInfo.asset?.symbol
            });
            
            // Use the provided owner wallet GUID if available, otherwise use the current wallet
            // This is important to ensure we're using the wallet that owns the asset
            // Use only currentWallet.guid as the wallet for swap
            // Log wallet information for debugging
            console.log("Wallet information:", {
                currentWalletGuid: currentWallet.guid
            });
            
            // Create the swap request with proper asset guids and wallet
            // Validate amount before proceeding
            const amount = Number(assetInInfo.amount);
            if (!assetInInfo.amount || isNaN(amount) || amount <= 0) {
                console.error("Invalid amount for swap:", assetInInfo.amount);
                return { status: "error", message: `Invalid amount: ${assetInInfo.amount}` };
            }

            const swapRequest = {
                walletGuid, // Use the correct wallet based on swap type
                amount, // Already validated
                assetInGuid: assetInInfo.asset.guid, // Use the input asset guid directly
                assetOutGuid: assetOutInfo.asset?.guid || "",
                slippagePercentage: String(slippagePercentage),
                mempadType: pad === "pumpfun" ? 0 : pad === "blumpump" ? 1 : 2
            };

            try {
                let response;
                // Use isMnemonics to determine wallet type
                if (currentWallet?.isMnemonics) {
                    // Internal wallet: always use memeSwapInternal
                    console.log("Executing internal swap:", swapRequest);
                    response = await WalletController.memeSwapInternal(swapRequest);
                    console.log("Internal swap response:", response);
                } else {
                    // External wallet: always use memeSwapExternal
                    console.log("Executing external swap:", swapRequest);
                    response = await WalletController.memeSwapExternal(swapRequest);
                    console.log("External swap response:", response);
                }
                return response;
            } catch (error: any) {
                console.error("Swap execution failed:", error);
                return { status: "error", message: error?.message || "Swap execution failed." };
            }
        }
    }))
);

// Subscribe to assetOutInfo.asset changes and update its balance
useMemeSwap.subscribe(
    (state) => state.assetOutInfo.asset,
    async (asset, prevAsset) => {
        if (asset !== prevAsset && asset) {
            await useMemeSwap.getState().updateMemeSwapAssetBalance();
            const balance = useMemeSwap.getState().assetOutInfo.balance;
            console.log('[MemeSwap] assetOutInfo.asset changed:', asset, 'balance:', balance);
        }
    }
);

// Subscribe to assetInInfo.asset changes and update its balance
useMemeSwap.subscribe(
    (state) => state.assetInInfo.asset,
    async (asset, prevAsset) => {
        if (asset !== prevAsset && asset) {
            await useMemeSwap.getState().updateMemeSwapInputAssetBalance();
            const balance = useMemeSwap.getState().assetInInfo.balance;
            console.log('[MemeSwap] assetInInfo.asset changed:', asset, 'balance:', balance);
        }
    }
);

// Subscribe to wallet changes and update all meme swap balances

useWallet.subscribe((state, prevState) => {
    if (state.currentWallet?.guid !== prevState.currentWallet?.guid) {
        console.log("[MemeSwap] Wallet changed, updating all meme swap balances...");
        const { assetInInfo, memeSwapTargetAddress } = useMemeSwap.getState();
        // If no meme token address, always force setAssetIn(null) to update TON balance
        if (!memeSwapTargetAddress) {
            useMemeSwap.getState().setAssetIn(null);
        } else if (!assetInInfo.asset) {
            useMemeSwap.getState().setAssetIn(null);
        } else {
            useMemeSwap.getState().updateAllMemeSwapBalances();
        }
    }
});

